import { Fragment, useEffect, useMemo, useRef, useState, type FormEvent } from 'react';
import type { Contact, Conversation, VavClient, Message } from '@vav/client-core';
import { MessageBubble, previewOf } from './MessageBubble.tsx';
import { SafetyNumberDialog } from './SafetyNumberDialog.tsx';
import { Avatar } from './Avatar.tsx';
import { useI18n } from '../i18n.tsx';
import { titleOf } from './Sidebar.tsx';
import {
  VoiceRecorder,
  micPermission,
  recordingSupported,
  watchMicPermission,
  type Recording,
} from '../recorder.ts';
import { formatDayMonth, formatFullDate } from '../format.ts';
import { AttachMenu } from './AttachMenu.tsx';
import { ForwardDialog } from './ForwardDialog.tsx';
import { GroupDialog } from './GroupDialog.tsx';
import { ContactDialog } from './ContactDialog.tsx';
import { formatDuration } from '@vav/protocol';

interface Props {
  client: VavClient;
  /** Every conversation, so a message can be forwarded into another one. */
  conversations: Conversation[];
  conversation: Conversation;
  messages: Message[];
  contactsById: Record<string, Contact>;
  presence: Record<string, boolean>;
  typing: string[];
  onSendText: (text: string, replyTo?: string) => Promise<void>;
  onSendFile: (file: File, caption?: string) => Promise<void>;
  onSendVoice: (recording: Recording) => Promise<void>;
  onBack: () => void;
  onStartCall?: (media: 'audio' | 'video') => void;
}

export function Thread({
  client,
  conversations,
  conversation,
  messages,
  contactsById,
  presence,
  typing,
  onSendText,
  onSendFile,
  onSendVoice,
  onBack,
  onStartCall,
}: Props) {
  const [draft, setDraft] = useState('');
  const [replyTo, setReplyTo] = useState<Message>();
  const [showSafety, setShowSafety] = useState(false);
  const [sending, setSending] = useState(false);
  const [recording, setRecording] = useState(false);
  /**
   * Selected message ids, or undefined when not selecting.
   *
   * Undefined rather than an empty array so "selection mode with nothing
   * picked" stays expressible -- that is the state right after unpicking the
   * last message, and collapsing it to "not selecting" would drop the user out
   * of the mode mid-task.
   */
  const [selection, setSelection] = useState<string[]>();
  const [forwarding, setForwarding] = useState<Message[]>();
  /** Catalogue key for why recording failed, or undefined. */
  const [micError, setMicError] = useState<
    | 'thread.voice.blocked'
    | 'thread.voice.noMicrophone'
    | 'thread.voice.blockedByOs'
    | 'thread.voice.busy'
    | 'thread.voice.sendFailed'
  >();
  /**
   * The browser's own name for the failure.
   *
   * Shown in small print rather than kept in the console, because the console
   * is unreachable on a phone and this is the one string that tells the two
   * remaining candidates apart from a photograph of the screen.
   */
  const [micDetail, setMicDetail] = useState<string>();
  /** Set once a send has been refused because the other person deleted their account. */
  const [recipientGone, setRecipientGone] = useState(false);
  /** Open date-time picker for a scheduled send. */
  const [scheduling, setScheduling] = useState(false);
  const [groupEditOpen, setGroupEditOpen] = useState(false);
  const [contactOpen, setContactOpen] = useState(false);
  /** The message being rewritten, when the composer is in edit mode. */
  const [editing, setEditing] = useState<Message>();

  function beginEdit(message: Message) {
    if (!('text' in message.content) || !message.content.text) return;
    setEditing(message);
    setDraft(message.content.text);
  }

  function cancelEdit() {
    setEditing(undefined);
    setDraft('');
  }
  const [scheduleAt, setScheduleAt] = useState('');

  async function submitScheduled() {
    const text = draft.trim();
    const when = new Date(scheduleAt).getTime();
    if (!text || !Number.isFinite(when) || when <= Date.now()) return;
    setScheduling(false);
    setDraft('');
    try {
      await client.scheduleText(conversation.id, text, when);
    } catch {
      setDraft(text);
    }
  }
  const [recordMs, setRecordMs] = useState(0);
  const recorderRef = useRef(new VoiceRecorder());
  const recordTimer = useRef<ReturnType<typeof setInterval> | undefined>(undefined);
  const canRecord = useMemo(() => recordingSupported(), []);
  const bottomRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const typingTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const { t, locale } = useI18n();

  const title = titleOf(conversation, contactsById, t('sidebar.group'));
  const online = conversation.peerId ? presence[conversation.peerId] : undefined;

  /**
   * How many were unread when this conversation was opened.
   *
   * Captured during render, because opening a thread calls `markRead` and the
   * count is zero within a tick. This is the only moment it can be read.
   */
  const openedWith = useRef<{ id: string; unread: number } | undefined>(undefined);
  if (openedWith.current?.id !== conversation.id) {
    openedWith.current = { id: conversation.id, unread: conversation.unread ?? 0 };
  }

  /**
   * The first message the reader has not seen, if any.
   *
   * Counted from the end over incoming messages only: `unread` counts what
   * arrived, and our own messages are never part of it.
   */
  const firstUnreadId = useMemo(() => {
    const count = openedWith.current?.unread ?? 0;
    if (count <= 0) return undefined;
    const incoming = messages.filter((message) => !message.outgoing);
    return incoming[incoming.length - count]?.id;
  }, [messages, conversation.id]);

  /** The conversation whose opening position has already been set. */
  const positionedFor = useRef<string | undefined>(undefined);

  /**
   * Opening a conversation lands on the newest message; after that, follow the
   * reader.
   *
   * These are one effect because they are the same decision made twice, and
   * splitting them is what broke it. Opening used to scroll on `conversation.id`
   * alone — which fires while the history is still loading, so it scrolled an
   * empty list and did nothing. By the time the messages arrived, the only
   * remaining rule was "stick to the bottom if already near it", and a list
   * freshly rendered at scrollTop 0 is not near it. So every conversation
   * opened at the oldest message it had, and had to be scrolled down by hand.
   */
  useEffect(() => {
    const list = listRef.current;
    if (!list) return;

    if (positionedFor.current !== conversation.id) {
      // Wait for the history rather than positioning an empty list.
      if (messages.length === 0) return;
      positionedFor.current = conversation.id;

      const place = () => {
        // Land on the first thing not yet read, so the reader picks up exactly
        // where they left off rather than at the newest message with unread
        // ones scrolled off above it.
        const unread = firstUnreadId
          ? list.querySelector(`[data-message="${firstUnreadId}"]`)
          : null;
        if (unread) unread.scrollIntoView({ block: 'start' });
        else list.scrollTop = list.scrollHeight;
      };

      /**
       * Keep re-placing while the thread is still growing.
       *
       * One correction is not enough, and this is why the fix kept looking like
       * it had not worked: a photo has no height until it decodes, which
       * happens well after the frame the messages were rendered in. Every image
       * that lands afterwards pushes the content down and leaves the view
       * stranded above where it was put — in a conversation with pictures,
       * which is every real conversation, it looks exactly like never having
       * scrolled at all.
       *
       * So the position is held for a short window rather than set once: any
       * media loading inside the list re-places it, and a size observer catches
       * whatever else changes height. Both stop after the window, so a reader
       * who scrolls away is never yanked back.
       */
      place();
      const stopAt = Date.now() + 2500;
      const hold = () => {
        if (Date.now() > stopAt) return;
        place();
      };

      list.addEventListener('load', hold, true);
      const observer = new ResizeObserver(hold);
      for (const child of list.children) observer.observe(child);
      const frames = [0, 60, 200, 500, 1200].map((delay) => setTimeout(hold, delay));

      return () => {
        list.removeEventListener('load', hold, true);
        observer.disconnect();
        frames.forEach(clearTimeout);
      };
    }

    // Stick to the bottom only when the reader is already there; yanking the
    // view down mid-scrollback is the single most annoying thing a chat app can
    // do.
    const nearBottom = list.scrollHeight - list.scrollTop - list.clientHeight < 160;
    if (nearBottom) bottomRef.current?.scrollIntoView({ block: 'end' });
  }, [messages, conversation.id, firstUnreadId]);

  useEffect(() => {
    setReplyTo(undefined);
    setDraft('');
    setRecipientGone(false);
  }, [conversation.id]);

  function onDraftChange(value: string) {
    setDraft(value);
    client.setTyping(conversation.id, true);
    clearTimeout(typingTimer.current);
    typingTimer.current = setTimeout(() => client.setTyping(conversation.id, false), 2500);
  }

  async function submit(event: FormEvent) {
    event.preventDefault();
    const text = draft.trim();
    if (!text || sending) return;

    setSending(true);
    setDraft('');
    clearTimeout(typingTimer.current);
    client.setTyping(conversation.id, false);
    try {
      if (editing) {
        if (editing.state === 'scheduled') await client.editScheduled(conversation.id, editing.id, text);
        else await client.editMessage(conversation.id, editing.id, text);
        setEditing(undefined);
      } else {
        await onSendText(text, replyTo?.id);
      }
      setReplyTo(undefined);
    } catch (err) {
      // The bubble is already marked failed by the client; put the text back so
      // the user does not lose what they typed.
      setDraft(text);
      // Nobody announces leaving, so this is the moment it can be known. Said
      // once, and kept on screen, rather than failing silently every time.
      if ((err as { code?: string })?.code === 'recipient_gone') setRecipientGone(true);
    } finally {
      setSending(false);
    }
  }

  /**
   * The in-flight `start()`, so a release can wait for it.
   *
   * Opening the microphone is asynchronous and the first time it also puts a
   * permission prompt on screen. A normal tap is far shorter than that, so the
   * release arrives while `start()` is still pending — and a release that
   * decided "nothing is recording" from React state would leave the recorder
   * running with the microphone open, and the button dead until it was pressed
   * a second time.
   */
  const startRef = useRef<Promise<void> | undefined>(undefined);

  async function startRecording() {
    if (recorderRef.current.active || startRef.current || !canRecord) return;
    setMicError(undefined);
    setMicDetail(undefined);
    const attempt = recorderRef.current.start();
    startRef.current = attempt;
    try {
      await attempt;
      setRecording(true);
      setRecordMs(0);
      recordTimer.current = setInterval(() => setRecordMs(recorderRef.current.elapsedMs), 100);
    } catch (err) {
      setRecording(false);
      /**
       * Say why. This used to fail silently, which is indistinguishable from a
       * broken button: the microphone is denied once — often by dismissing the
       * prompt — and from then on every press does nothing at all, with nothing
       * on screen to suggest the permission is the problem or that it is
       * recoverable.
       *
       * The two cases need different advice. Denied is fixed in browser
       * settings; absent hardware is not fixed at all.
       */
      const name = (err as { name?: string })?.name;
      setMicDetail(name);
      if (name === 'NotFoundError' || name === 'OverconstrainedError') {
        setMicError('thread.voice.noMicrophone');
      } else if (name === 'NotReadableError' || name === 'AbortError') {
        /**
         * The permission is fine and the hardware will not open.
         *
         * Almost always another app holding the microphone -- a call, a voice
         * assistant, a recorder left running. Nothing about permissions will
         * fix it, which is exactly why it must not be reported as a permission
         * problem.
         */
        setMicError('thread.voice.busy');
      } else {
        /**
         * Ask *where* the refusal came from, because there are two answers and
         * the remedies have nothing in common.
         *
         * If this site's permission is already `granted` or still `prompt` and
         * the request failed anyway, the site is not what refused: the browser
         * itself has no microphone permission from the operating system, and no
         * amount of changing site settings will help. Telling someone in that
         * state to look in site settings sends them somewhere that already says
         * "allowed", which is exactly the dead end to avoid.
         */
        const state = await micPermission();
        setMicError(state === 'denied' ? 'thread.voice.blocked' : 'thread.voice.blockedByOs');
      }
    } finally {
      startRef.current = undefined;
    }
  }

  function stopTimer() {
    clearInterval(recordTimer.current);
    recordTimer.current = undefined;
    setRecording(false);
    setRecordMs(0);
  }

  function cancelRecording() {
    const pending = startRef.current;
    recorderRef.current.cancel();
    stopTimer();
    // The microphone may still be opening. Cancelling that is not possible, so
    // tear down again once it lands — otherwise the stream stays live with
    // nothing left holding a reference to it, and the browser keeps showing the
    // recording indicator.
    if (pending) void pending.catch(() => undefined).then(() => recorderRef.current.cancel());
  }

  async function finishRecording() {
    // Wait for a start that may still be resolving, then ask the recorder
    // itself rather than React state — the state is a render behind.
    await startRef.current?.catch(() => undefined);
    if (!recorderRef.current.active) return;
    const result = await recorderRef.current.stop();
    stopTimer();
    // `stop` returns nothing when the press was too short to be a message.
    if (!result) return;
    try {
      await onSendVoice(result);
    } catch (err) {
      // Encrypting, uploading and sending can all fail. This was called as
      // `void finishRecording()` from a pointer handler, so a rejection here
      // became an unhandled promise and the recording simply disappeared --
      // held, released, and nothing in the thread to show it ever happened.
      //
      // Logged as well as shown: the notice has to stay short and general, and
      // the underlying reason is the only thing that makes a report like "the
      // microphone does not work" diagnosable at all.
      console.warn('[vav] sending the recording failed', err);
      setMicError('thread.voice.sendFailed');
    }
  }

  // Never leave the microphone open because the thread was closed mid-record.
  useEffect(() => () => cancelRecording(), []);

  /**
   * Clear the warning the moment the permission is actually granted.
   *
   * Someone who follows the instructions and comes back to a screen still
   * saying the microphone is blocked concludes the instructions were wrong.
   */
  useEffect(
    () => watchMicPermission((state) => state === 'granted' && setMicError(undefined)),
    [],
  );

  // Leaving the thread must not leave an invisible selection behind.
  useEffect(() => {
    setSelection(undefined);
    setForwarding(undefined);
  }, [conversation.id]);

  function toggleSelected(message: Message) {
    setSelection((prev) => {
      const current = prev ?? [];
      return current.includes(message.id)
        ? current.filter((id) => id !== message.id)
        : [...current, message.id];
    });
  }

  const selectedMessages = messages.filter((message) => selection?.includes(message.id));

  /**
   * The date tab: while the reader scrolls, a small tab slides out of the
   * right edge naming the day of the topmost visible message, and slides back
   * a second after the scrolling stops. Same side in every language.
   */
  const [dateTab, setDateTab] = useState<{ label: string; shown: boolean }>();
  const dateTabTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const byIdRef = useRef<Record<string, Message>>({});
  /* Read through a ref so the scroll effect binds once per conversation. With
     `t` in the dependency list the effect re-bound on every provider render --
     which a message arriving causes -- and its cleanup wiped the tab the
     moment anything else happened. */
  const tRef = useRef(t);
  tRef.current = t;

  useEffect(() => {
    const list = listRef.current;
    if (!list) return;
    let raf = 0;

    const startOfDay = (ts: number) => {
      const day = new Date(ts);
      day.setHours(0, 0, 0, 0);
      return day.getTime();
    };
    const labelFor = (ts: number) => {
      const today = startOfDay(Date.now());
      const day = startOfDay(ts);
      if (day === today) return tRef.current('thread.date.today');
      // Via Date rather than `today - 86400000`: a DST switch makes a day
      // that is not 24 hours long, and the subtraction lands mid-day.
      const yesterday = new Date(today);
      yesterday.setDate(yesterday.getDate() - 1);
      if (day === yesterday.getTime()) return tRef.current('thread.date.yesterday');
      return new Date(ts).getFullYear() === new Date().getFullYear()
        ? formatDayMonth(ts)
        : formatFullDate(ts);
    };

    const onScroll = () => {
      if (raf) return;
      raf = requestAnimationFrame(() => {
        raf = 0;
        const rows = list.querySelectorAll<HTMLElement>('[data-message]');
        if (rows.length === 0) return;
        // Binary search for the topmost visible row; rows are in document
        // order, so "first whose bottom edge is below the viewport top".
        const top = list.getBoundingClientRect().top;
        let low = 0;
        let high = rows.length - 1;
        let found = rows.length - 1;
        while (low <= high) {
          const mid = (low + high) >> 1;
          if (rows[mid].getBoundingClientRect().bottom >= top) {
            found = mid;
            high = mid - 1;
          } else {
            low = mid + 1;
          }
        }
        const id = rows[found].dataset.message;
        const ts = id ? byIdRef.current[id]?.ts : undefined;
        if (!ts) return;
        setDateTab({ label: labelFor(ts), shown: true });
        clearTimeout(dateTabTimer.current);
        dateTabTimer.current = setTimeout(
          () => setDateTab((current) => current && { ...current, shown: false }),
          1000,
        );
      });
    };

    list.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      list.removeEventListener('scroll', onScroll);
      cancelAnimationFrame(raf);
      clearTimeout(dateTabTimer.current);
      setDateTab(undefined);
    };
  }, [conversation.id]);

  /** Messages by id, so a reply can show what it answers. */
  const byId = useMemo(() => {
    const map: Record<string, Message> = {};
    for (const message of messages) map[message.id] = message;
    byIdRef.current = map;
    return map;
  }, [messages]);

  /**
   * Scroll to the quoted message and mark it briefly.
   *
   * Highlighted rather than merely scrolled to: after the list jumps, "which
   * one was it" is the immediate next question, and a message that is already
   * on screen would otherwise produce no visible response to the tap at all.
   */
  function jumpTo(messageId: string) {
    const element = listRef.current?.querySelector(`[data-message="${messageId}"]`);
    if (!element) return;
    element.scrollIntoView({ block: 'center', behavior: 'smooth' });
    element.classList.add('is-flash');
    setTimeout(() => element.classList.remove('is-flash'), 1400);
  }

  async function forwardTo(conversationIds: string[]) {
    const chosen = forwarding ?? [];
    setForwarding(undefined);
    setSelection(undefined);
    // Oldest first, so a forwarded run arrives in the order it was written
    // rather than reversed.
    const ordered = [...chosen].sort((a, b) => a.ts - b.ts);
    for (const target of conversationIds) {
      for (const message of ordered) {
        try {
          await client.forwardMessage(conversation.id, message.id, target);
        } catch {
          // One unforwardable message must not abandon the rest of the batch.
        }
      }
    }
  }

  async function deleteSelectedForMe() {
    for (const message of selectedMessages) {
      await client.deleteMessageForMe(conversation.id, message.id);
    }
    setSelection(undefined);
  }

  const typingNames = typing
    .map((id) => contactsById[id]?.displayName ?? id)
    .filter(Boolean);

  // Intl.ListFormat, not join(', '). Languages disagree on the separator and on
  // whether the last item takes a conjunction: "a, b and c" in English,
  // "a، b وc" in Arabic.
  const typingList = new Intl.ListFormat(locale, { style: 'long', type: 'conjunction' }).format(
    typingNames,
  );

  if (selection) {
    return (
      <section className="thread">
        <header className="thread__header thread__header--selecting">
          <button
            className="icon"
            onClick={() => setSelection(undefined)}
            aria-label={t('select.cancel')}
          >
            ✕
          </button>
          <strong className="thread__selectCount">
            {t('select.count', { count: selection.length })}
          </strong>
          <button
            className="icon"
            disabled={selection.length === 0}
            onClick={() => setForwarding(selectedMessages)}
            aria-label={t('bubble.forward')}
            title={t('bubble.forward')}
          >
            ➦
          </button>
          <button
            className="icon"
            disabled={selection.length === 0}
            onClick={() => void deleteSelectedForMe()}
            aria-label={t('bubble.deleteForMe')}
            title={t('bubble.deleteForMe')}
          >
            🗑️
          </button>
        </header>

        <div className="thread__messages" ref={listRef}>
          {messages.map((message, index) => (
            <MessageBubble
              key={message.id}
              client={client}
              message={message}
              senderName={contactsById[message.senderId]?.displayName ?? message.senderId}
              showSender={
                conversation.kind === 'group' && messages[index - 1]?.senderId !== message.senderId
              }
              onReply={setReplyTo}
              onReact={(m, emoji) => void client.react(conversation.id, m.id, emoji)}
              onDelete={(m) => void client.deleteMessage(conversation.id, m.id)}
              onDeleteForMe={(m) => void client.deleteMessageForMe(conversation.id, m.id)}
              onForward={(m) => setForwarding([m])}
              repliedTo={message.replyTo ? byId[message.replyTo] : undefined}
              repliedToName={
                message.replyTo
                  ? (contactsById[byId[message.replyTo]?.senderId ?? '']?.displayName ?? undefined)
                  : undefined
              }
              onSelect={(m) => setSelection([m.id])}
              selected={selection.includes(message.id)}
              onToggleSelected={toggleSelected}
            />
          ))}
          <div ref={bottomRef} />
        </div>

        {forwarding && (
          <ForwardDialog
            conversations={conversations}
            contactsById={contactsById}
            count={forwarding.length}
            excludeId={conversation.id}
            onConfirm={(ids) => void forwardTo(ids)}
            onClose={() => setForwarding(undefined)}
          />
        )}
      </section>
    );
  }

  return (
    <section className="thread">
      <header className="thread__header">
        <button className="icon thread__back" onClick={onBack} aria-label={t('thread.back')}>
          ›
        </button>
        {conversation.kind === 'group' ? (
          <button
            className="thread__groupButton"
            onClick={() => setGroupEditOpen(true)}
            aria-label={t('group.edit.title')}
            title={t('group.edit.title')}
          >
            <Avatar name={title} group online={online} size={40} avatar={conversation.avatar} client={client} />
          </button>
        ) : (
          <button
            className="thread__groupButton"
            onClick={() => setContactOpen(true)}
            aria-label={t('contact.title')}
            title={t('contact.title')}
          >
            <Avatar
              name={title}
              online={online}
              size={40}
              avatar={conversation.peerId ? contactsById[conversation.peerId]?.avatar : undefined}
              client={client}
            />
          </button>
        )}
        <div className="thread__title">
          <strong>{title}</strong>
          <small className="muted">
            {typingNames.length > 0
              ? t('thread.typing', { count: typingNames.length, names: typingList })
              : conversation.kind === 'group'
                ? t('thread.members', { count: conversation.memberIds.length })
                : t(online ? 'status.online' : 'status.offline')}
          </small>
        </div>
        {/* Calls are capped at four people, me included; a bigger group simply
            has no call buttons rather than a button that apologises. */}
        {onStartCall && conversation.memberIds.length <= 3 && (
          <>
            <button
              className="icon"
              onClick={() => onStartCall('audio')}
              title={t('thread.call.audio')}
              aria-label={t('thread.call.audio')}
            >
              📞
            </button>
            <button
              className="icon"
              onClick={() => onStartCall('video')}
              title={t('thread.call.video')}
              aria-label={t('thread.call.video')}
            >
              🎥
            </button>
          </>
        )}
        {conversation.peerId && (
          <button className="icon" onClick={() => setShowSafety(true)} title={t('thread.verifySafety')}>
            🛡️
          </button>
        )}
      </header>

      <div className="thread__messages" ref={listRef}>
        <div className="thread__notice">
          <span>🔒 {t('thread.encrypted')}</span>
        </div>

        {messages.map((message, index) => (
          <Fragment key={message.id}>
            {/* Where reading stopped last time. */}
            {message.id === firstUnreadId && (
              <div className="unreadLine">
                <span>{t('thread.unreadFrom', { count: openedWith.current?.unread ?? 0 })}</span>
              </div>
            )}
          <MessageBubble
            key={message.id}
            client={client}
            message={message}
            senderName={contactsById[message.senderId]?.displayName ?? message.senderId}
            showSender={
              conversation.kind === 'group' && messages[index - 1]?.senderId !== message.senderId
            }
            onReply={setReplyTo}
            onReact={(m, emoji) => void client.react(conversation.id, m.id, emoji)}
            onDelete={(m) => void client.deleteMessage(conversation.id, m.id)}
            onDeleteForMe={(m) => void client.deleteMessageForMe(conversation.id, m.id)}
            onForward={(m) => setForwarding([m])}
            onCancelScheduled={(m) => void client.cancelScheduled(conversation.id, m.id).catch(() => undefined)}
            onEdit={beginEdit}
            repliedTo={message.replyTo ? byId[message.replyTo] : undefined}
            repliedToName={
              message.replyTo
                ? (contactsById[byId[message.replyTo]?.senderId ?? '']?.displayName ?? undefined)
                : undefined
            }
            onJumpTo={jumpTo}
            onSelect={(m) => setSelection([m.id])}
          />
          </Fragment>
        ))}
        <div ref={bottomRef} />
      </div>

      {dateTab && (
        <div className="thread__dateTabClip" aria-hidden="true">
          <div className={'thread__dateTab' + (dateTab.shown ? ' thread__dateTab--shown' : '')}>
            {dateTab.label}
          </div>
        </div>
      )}

      {replyTo && (
        <div className="replyBar">
          <span className="replyBar__text" dir="auto">
            {/* Same wording as the quote that will appear on the sent message,
                so the composer does not promise "Attachment" and then show
                "Photo". */}
            {t('thread.replyingTo', { preview: previewOf(replyTo, t) })}
          </span>
          <button className="icon" onClick={() => setReplyTo(undefined)} aria-label={t('thread.cancelReply')}>
            ✕
          </button>
        </div>
      )}

      {(recipientGone || conversation.peerGone) && (
        <div className="goneBanner" role="alert">
          <span className="goneBanner__mark" aria-hidden="true">⛔</span>
          <div>
            <strong>{t('thread.recipientGone.title')}</strong>
            <p>{t('thread.recipientGone')}</p>
          </div>
        </div>
      )}

      {editing && (
        <div className="replyBar">
          <span className="replyBar__text">✏️ {t('thread.editing')}</span>
          <button className="icon" onClick={cancelEdit} aria-label={t('profile.name.cancel')}>
            ✕
          </button>
        </div>
      )}

      {micError && (
        <div className="micNotice" role="alert">
          <div className="micNotice__body">
            <strong>{t(micError)}</strong>
            {/* The generic "check your settings" is useless on a phone, where
                the setting is three menus deep and named differently in the
                installed app than in the browser. */}
            {micError === 'thread.voice.blocked' && (
              <span className="muted">{t('thread.voice.blocked.how')}</span>
            )}
            {micError === 'thread.voice.blockedByOs' && (
              <span className="muted">{t('thread.voice.blockedByOs.how')}</span>
            )}
            {micError === 'thread.voice.busy' && (
              <span className="muted">{t('thread.voice.busy.how')}</span>
            )}
            {micDetail && <span className="micNotice__detail">{micDetail}</span>}
          </div>
          <span className="micNotice__actions">
            {micError !== 'thread.voice.noMicrophone' && (
              <button onClick={() => void startRecording()}>{t('thread.voice.retry')}</button>
            )}
            <button
              className="icon"
              onClick={() => setMicError(undefined)}
              aria-label={t('thread.cancelReply')}
            >
              ✕
            </button>
          </span>
        </div>
      )}

      {/* No composer for a conversation with nobody at the other end: the
          banner above is the whole truth, and an input under it would promise
          a delivery that cannot happen. History stays readable. */}
      {!(recipientGone || conversation.peerGone) && (
      <form className="composer" onSubmit={submit}>
        <AttachMenu
          disabled={recording}
          onPick={(file) => {
            void onSendFile(file, draft.trim() || undefined);
            setDraft('');
          }}
        />
        {recording ? (
          <div className="recording">
            <span className="recording__dot" aria-hidden="true" />
            <span className="recording__time">{formatDuration(recordMs)}</span>
            <span className="recording__hint muted">{t('thread.recording')}</span>
            <button
              type="button"
              className="icon"
              onClick={cancelRecording}
              aria-label={t('thread.cancelRecording')}
            >
              🗑️
            </button>
          </div>
        ) : (
          <textarea
            value={draft}
            onChange={(e) => onDraftChange(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                void submit(e);
              }
            }}
            placeholder={t('thread.composer')}
            rows={1}
            dir="auto"
          />
        )}

        {draft.trim() ? (
          <>
            <button
              type="button"
              className="icon composer__clock"
              onClick={() => {
                // Default to five minutes out: a valid value the picker can
                // adjust beats an empty field that refuses to submit.
                const d = new Date(Date.now() + 5 * 60_000);
                d.setSeconds(0, 0);
                setScheduleAt(new Date(d.getTime() - d.getTimezoneOffset() * 60_000).toISOString().slice(0, 16));
                setScheduling(true);
              }}
              aria-label={t('schedule.title')}
              title={t('schedule.title')}
            >
              🕐
            </button>
          <button type="submit" className="send" disabled={sending} aria-label={t('thread.send')}>
            ➤
          </button>
          </>
        ) : !canRecord ? (
          // Browsers withhold the microphone entirely outside a secure context,
          // so over plain HTTP on a LAN there is nothing to enable. Say why
          // rather than leaving a dead button or an unexplained gap.
          <button
            type="button"
            className="send send--mic"
            disabled
            title={t('thread.voice.unavailable.title')}
            aria-label={t('thread.voice.unavailable.label')}
            onClick={() => undefined}
          >
            🎤
          </button>
        ) : (
          <button
            type="button"
            className={`send send--mic ${recording ? 'is-recording' : ''}`}
            aria-label={recording ? t('thread.voice.stop') : t('thread.voice.start')}
            title={recording ? t('thread.voice.stop') : t('thread.voice.start')}
            // Press-and-hold like WhatsApp, but click-to-toggle also works —
            // holding a mouse button down for a minute is not a real gesture.
            //
            // The pointer is captured so the release is delivered here even if
            // the finger drifts off the button, which on a phone it always
            // does. Without capture the drift fired `pointerleave` and the
            // release landed on whatever was underneath, so a recording either
            // ended early or never ended at all.
            onPointerDown={(e) => {
              // Capture first, but never let it stop the recording. It throws
              // if the pointer is already gone, and this handler is the only
              // thing that starts recording at all -- so a throw here used to
              // mean the button did nothing, silently, with no error to show
              // for it.
              try {
                e.currentTarget.setPointerCapture(e.pointerId);
              } catch {
                // Without capture a finger sliding off the button loses the
                // release; `pointercancel` and the thread unmount both still
                // stop the recorder, so this degrades rather than breaks.
              }
              void startRecording();
            }}
            onPointerUp={() => void finishRecording()}
            /*
              The system can take the pointer away mid-press: a notification, a
              call, the browser deciding the gesture was a scroll after all.
              Leaving the microphone open through that is the one outcome to
              avoid -- but throwing away a recording that is already long enough
              to be a message is nearly as bad, and the browser fires this event
              on its own judgement rather than the user's. So anything worth
              keeping is sent, and only a stray tap is discarded.
            */
            onPointerCancel={() => {
              if (recorderRef.current.active && recorderRef.current.elapsedMs >= 350) {
                void finishRecording();
              } else {
                cancelRecording();
              }
            }}
          >
            {recording ? '⏹' : '🎤'}
          </button>
        )}
      </form>
      )}

      {contactOpen && conversation.peerId && contactsById[conversation.peerId] && (
        <ContactDialog
          client={client}
          contact={contactsById[conversation.peerId]}
          online={online}
          onClose={() => setContactOpen(false)}
        />
      )}

      {groupEditOpen && (
        <GroupDialog client={client} conversation={conversation} onClose={() => setGroupEditOpen(false)} />
      )}

      {scheduling && (
        <div className="overlay" onClick={() => setScheduling(false)}>
          <div className="card dialog" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
            <header className="dialog__header">
              <h2>{t('schedule.title')}</h2>
              <button className="icon" onClick={() => setScheduling(false)} aria-label={t('profile.name.cancel')}>
                ✕
              </button>
            </header>
            <p className="hint">{t('schedule.explain')}</p>
            <input
              type="datetime-local"
              value={scheduleAt}
              min={new Date(Date.now() - new Date().getTimezoneOffset() * 60_000).toISOString().slice(0, 16)}
              onChange={(e) => setScheduleAt(e.target.value)}
            />
            <button className="primary" onClick={() => void submitScheduled()}>
              {t('schedule.confirm')}
            </button>
          </div>
        </div>
      )}

      {forwarding && (
        <ForwardDialog
          conversations={conversations}
          contactsById={contactsById}
          count={forwarding.length}
          excludeId={conversation.id}
          onConfirm={(ids) => void forwardTo(ids)}
          onClose={() => setForwarding(undefined)}
        />
      )}

      {showSafety && conversation.peerId && (
        <SafetyNumberDialog
          client={client}
          userId={conversation.peerId}
          name={title}
          onClose={() => setShowSafety(false)}
        />
      )}
    </section>
  );
}
